// TabContent1.js
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, InputBase } from '@mui/material';
import { tokens } from '../../../../../theme';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShDateRangePicker from "../../../../theme/ShDateRangePicker";
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { ENV_TYPES } from "../../../Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { yupNumberRequired, yupRequired } from '../../../../../utils/validation';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import { useGetWebshopCredentialsSelectQuery } from '../../../../../newapi/webshop/webshopSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import { useGetOrderFieldsSelectQuery } from '../../../../../newapi/order/orderFieldSlice';
import { booleanValueTypes, dropdownValueTypes, numberValueTypes, textValueTypes } from '../../../../Streams/Conditions/ConditionSettings';
import TextInput from '../../../../theme/inputs/TextInput';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { buildPickupMethodRemark, parsePickupMethodRemark } from '../../../../../utils/helpers';
import Subheading from '../../../../theme/text/Subheading';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import EMGeneralExplainerBox from '../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import { useGetSettingsForRelationQuery } from '../../../../../newapi/global/generalApiSlice';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const [loaded, setLoaded] = useState(false);
    const dispatch = useDispatch();
    const { data: relations, isLoading } = useGetRelationsSelectQuery();

    // Get values to determine if the relation dropdown should be shown
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    const [canCreateB2B, setCanCreateB2B] = useState(false);
    const {data: relationSettings, isLoading: isLoadingSettings} = useGetSettingsForRelationQuery();

    useEffect(() => {
        if (relationSettings) {
            setCanCreateB2B(relationSettings?.find(setting => setting.key === "relation_allow_b2b")?.value === "true" ?? false)
        }
    }, [relationSettings]);

    const handleInput = (key, val) => {

        props.setLocalData(prev => ({
            ...prev,
            [key]: val,
            ...(key === "relationId" && { webshopCredentialsId: null }),
        }));
        // props.handleDataUpdate({ [key]: val, ...(key === "relationId" && { webshopCredentialsId: null }) });
        if (key === "isPickup") {

            props.setLocalData(prev => ({
                ...prev,
                deliveryMethod: val ? "pickup" : "regular",
                ...(!val && { remark: parsePickupMethodRemark(prev.remark).remark }),
            }));
            // props.handleDataUpdate({ deliveryMethod: val ? "pickup" : "regular" });
        }
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    // The pickup method is stored at the end of the remark
    const pickupRemark = parsePickupMethodRemark(props.localData.remark);
    const pickupMethodOptions = [
        { id: "boxes", name: t("loose_boxes") },
        { id: "pallet", name: t("pallet") },
    ];
    const handlePickupMethodInput = (key, val) => {
        const next = { ...pickupRemark, [key]: val };
        handleInput("remark", buildPickupMethodRemark(next.remark, next.pickupMethod, next.maxHeight));
    };

    const select = (date) => {
        const formattedDate = date[0].formattedStartDate;
        props.handleDataUpdate({ date_planned: formattedDate });
    }

    const {data: orderFields, isLoading: orderFieldsLoading, error: orderFieldsError} = useGetOrderFieldsSelectQuery();
    // Set virtual product is true for POST request
    // useEffect(() => {
    //     if(loaded) {
    //         props.handleDataUpdate({ isVirtual: true });
    //     }
    //     setLoaded(true);
    // }, [loaded]);

    useEffect(() => {
        if (loaded) {
            props.setLocalData(prev => ({
                ...prev,
                isVirtual: true
            }));
        }
        setLoaded(true);
    }, [loaded]);

    return (
        <>
            <Box 
                sx={{
                    display:'flex',
                    flexDirection:"column",
                }}
            >
                {isFulfilment && !isRelation && (
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        paddingTop: 1.5,
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                    }}
                >
                    
                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("relation")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:40
                        }}
                    >
                        <ShValidatedInputDropdown
                            key={props.localData.relationId}
                            displayName={"name"} 
                            changeField={"id"}
                            valueKey={"id"}
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_relation_placeholder")
                            }}
                            styles={{
                                minWidth:300
                            }}

                            disallowDeselection
                            name="relationId"
                            selected={relations?.find(item => item.id === props.localData.relationId)?.id ?? -1}
                            options={relations ? relations : []}
                            noSelection={t("choose_option")} 
                            onChange={handleInput}
                            error={props.errors.relationId}
                        />
                    </Box>
                </Box>
                )}


                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("reference")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShValidatedInput
                            name="webshopreference"
                            value={props.localData.webshopreference}
                            onChange={handleInput}
                            error={props.errors.webshopreference}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("date_planned")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShDateRangePicker 
                            singleSelect={true} 
                            select={select} 
                            size={32}
                        >

                        </ShDateRangePicker>
                    </Box>
                    
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("pickup_order")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                    <ShSwitch
                        checked={props.localData.isPickup ?? false}
                        onChange={(e) => handleInput('isPickup', e.target.checked)}
                    />
                    </Box>
                    
                </Box>
                {props.localData.isPickup && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("pickup_method")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                borderRadius:4,
                                display:"flex",
                            }}
                        >
                            <ShValidatedInputDropdown
                                displayName={"name"}
                                valueKey={"id"}
                                closeOnSelection
                                disallowDeselection
                                name="pickupMethod"
                                selected={pickupRemark.pickupMethod ?? -1}
                                options={pickupMethodOptions}
                                noSelection={t("choose_option")}
                                onChange={handlePickupMethodInput}
                            />
                        </Box>
                    </Box>
                )}
                {props.localData.isPickup && pickupRemark.pickupMethod === "pallet" && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("max_height")} (cm)</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                            }}
                        >
                            <NumberInput
                                btnSize={"24"}
                                height={32}
                                maxWidth={160}
                                onlyPositive
                                min={0}
                                value={pickupRemark.maxHeight ?? ''}
                                change={(val) => handlePickupMethodInput("maxHeight", val)}
                            />
                        </Box>
                    </Box>
                )}
                {canCreateB2B && (
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("b2b_order")}</Text>
                        <EMGeneralExplainerBox title={t('em_b2b_title')} text={t('em_b2b_text')}/>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                    <ShSwitch
                        checked={props.localData.isB2B ?? false}
                        onChange={(e) => handleInput('isB2B', e.target.checked)}
                    />
                    </Box>
                </Box>
                )}
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        paddingTop: 1.5,
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("remark")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="remark"
                            value={pickupRemark.remark}
                            onChange={handlePickupMethodInput}
                            error={props.errors.remark}
                        />
                    </Box>
                </Box>

                {orderFields && (

                    <Box
                        sx={{
                            mx:"-12px",
                            px:1.5,
                            pt:1,
                            pb:1.5,
                            mt:3,
                            background:colors.grey[50],
                            display:"flex",
                            gap:1,
                            flexDirection:"column"
                        }}
                    >
                        <Subheading>{t("custom_fields")}</Subheading>
                        {props.orderFieldValues.map((field,idx) => (
                            <Box
                                key={"field"+idx}
                                sx={{
                                    display:"flex",
                                    alignItems:'center',
                                    justifyContent:'space-between',
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        mr:7,
                                    }}
                                >
                                    <Text>{field.name}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        flex:1,
                                    }}
                                >
                                    {(textValueTypes.includes(field.valueType) || numberValueTypes.includes(field.valueType)) &&
                                        
                                        <Box
                                            sx={{
                                                background: colors.grey[100],
                                                height: 32,
                                                borderRadius: 4,
                                                width: 1,
                                                "&:hover": {
                                                    background: colors.grey[200],
                                                    cursor: "pointer",
                                                },
                                            }}
                                        >
                                            <TextInput
                                                type={textValueTypes.includes(field.valueType) ? "text" : "number"}
                                                value={field.value}
                                                background="transparent"
                                                onChange={(e) => {
                                                    const newValue = e.target.value;

                                                    props.setOrderFieldValues(prev =>
                                                        prev.map(f =>
                                                        f.orderFieldId === field.orderFieldId
                                                            ? { ...f, value: newValue } // update the value
                                                            : f
                                                        )
                                                    );
                                                }}
                                                
                                            />
                                            
                                        </Box>
                                    }
                                    {booleanValueTypes.includes(field.valueType) &&
                                        <Box>
                                            <ShSwitch
                                                checked={!!field.value}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    props.setOrderFieldValues((prev) =>
                                                        prev.map((f) =>
                                                            f.orderFieldId === field.orderFieldId
                                                            ? { ...f, value: checked }
                                                            : f
                                                        )
                                                    );
                                                }}
                                            />
                                        </Box>
                                    }
                                    {dropdownValueTypes.includes(field.valueType) &&
                                        <Box
                                            sx={{
                                                background: colors.grey[100],
                                                borderRadius: 4,
                                                width: 1,
                                            }}
                                        >
                                            <InputDropdown
                                                searchOptions={{
                                                    enabled: true,
                                                    placeholder: t("search")
                                                }}
                                                closeOnSelection
                                                selected={field.listValues?.findIndex(item => item === field.value) ?? -1}
                                                options={field.listValues ?? []}
                                                onChange={(selectedIndex) => {
                                                    const selectedValue = field.listValues[selectedIndex];

                                                    props.setOrderFieldValues(prev =>
                                                        prev.map(f =>
                                                            f.orderFieldId === field.orderFieldId
                                                                ? { ...f, value: selectedValue }
                                                                : f
                                                        )
                                                    );
                                                }}
                                            />
                                        </Box>
                                    }

                                </Box>
                                <ButtonBase
                                    onClick={() => {
                                        props.setOrderFieldValues(prev => prev.filter(f => f.orderFieldId !== field.orderFieldId));
                                    }}
                                    sx={{
                                        width:32,
                                        height:32,
                                        color:colors.red[400],
                                        ml:1,
                                        borderRadius:4,
                                    }}
                                >
                                    <DeleteOutlineOutlinedIcon/>
                                </ButtonBase>
                            </Box>
                        ))}
                        <InputDropdown
                            searchOptions={{
                                enabled:true,
                                placeholder:t("search")
                            }}
                            resetOnSelection
                            closeOnSelection
                            selected={-1}
                            options={orderFields}
                            onChange={(selectedIndex) => {
                                const selectedField = orderFields[selectedIndex];

                                props.setOrderFieldValues((prev) => {
                                    const exists = prev.some(f => f.orderFieldId === selectedField.id);
                                    if (exists) return prev;

                                    return [
                                        ...prev,
                                        {
                                            orderFieldId: selectedField.id,
                                            name: selectedField.name,
                                            valueType: selectedField.valueType,
                                            listValues: selectedField.listValues ?? [],
                                            ...(selectedField.valueType === 'boolean' ? { value: false } : {}),
                                        }
                                    ];
                                });
                            }}
                        />
                    </Box>
                )}
                                
            </Box>
        </>
    );
};

const Details = (props) => {
    const {t} = useTranslation();
    const schema = yup.object({
        webshopreference: yup.string().min(5).max(35).required(t('webshopreference'))
    });

    const data = useSelector((state) => state.progressiontabs.data);
    const [localData, setLocalData] = useState(data);
    const [orderFieldValues, setOrderFieldValues] = useState(data.orderFieldValues ?? []);

    useEffect(() => {
        setLocalData(data);
    },[data])
    
    useEffect(() => {
        setLocalData(prev => ({
            ...prev,
            orderFieldValues: orderFieldValues
        }));
    }, [orderFieldValues]);

    return (
        <ProgressionTab 
            tabId="tabDetails" 
            subtitle={t("Details")} 
            step={1}
            nextTab={"tabAddressDetails"}
            maxSteps={3}
            schema={schema}
            updateDataWithValidation={localData} //updates pre-tab-switch
        >
            <Content
                localData={localData} 
                setLocalData={setLocalData}
                orderFieldValues={orderFieldValues}
                setOrderFieldValues={setOrderFieldValues}
            />
        </ProgressionTab>
    )
}

export default Details;

